import React, {useState, useEffect, useRef} from 'react';
import * as THREE from 'three';
import OrbitControls from 'three-orbitcontrols';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { BlockingLoader, NonBlockingLoader } from './loader/Loader';
import OPTIONS from './options.json'

const availableOptions = OPTIONS

export default function Scene(props){

  const [modelLoaded, setModelLoaded] = useState(false);
  const [animationFinished, setAnimationFinished] = useState(false);

  const [assetLoading, setAssetLoading] = useState(false);
  const [activeOption, setActiveOption] = useState('arm');

  const [armSelected, setArmSelected] = useState(props.initialSelection['arm'] || 0)
  const [insertSelected, setInsertSelected] = useState(props.initialSelection['insert'] || 0)
  const [selectedColor, setSelectedColor] = useState(props.initialSelection['color'] || 0)

  const variations = {
    "arm": [armSelected, setArmSelected], 
    "insert": [insertSelected, setInsertSelected], 
    "paint": [selectedColor, setSelectedColor]
  }

  const material = useRef(null);

  useEffect(() => {
    const activeOption = availableOptions.paint[selectedColor]
    material.current = new THREE.MeshStandardMaterial({ 
      color: parseInt(`0x${activeOption.color}`), 
      roughness: activeOption.roughness, 
      metalness: activeOption.metalness
    });
    if (model.current != null){
      model.current.traverse(o => {
        if (o.isMesh && o.name != null && (!o.name.includes("rim"))) {
          o.material = material.current;
        }
      });
    }
    if(props.hanldeSetColorVariation){
      props.hanldeSetColorVariation(selectedColor)
    }
  }, [selectedColor])


  const canvasElement = useRef(null);
  const sceneElement = useRef(null)
  const model = useRef(null);

  useEffect(() => {
    const MODEL_PATH = props.base_url + "rim.glb";

    const scene = new THREE.Scene();

    scene.background = new THREE.Color(0x171616);

    const renderer = new THREE.WebGLRenderer({ 'canvas': canvasElement.current, antialias: true });

    renderer.shadowMap.enabled = true;
    renderer.setPixelRatio(window.devicePixelRatio);
    sceneElement.current.appendChild(renderer.domElement);

    var camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 0;
    camera.position.y = 0;
    camera.position.x = 1200;
    camera.far = 10000

    var loader = new GLTFLoader();

    loader.load(MODEL_PATH, (gltf) => {
      model.current = gltf.scene;

      model.current.traverse(o => {
        if (o.isMesh) {
          o.castShadow = true;
          o.receiveShadow = true;
        }
      });

      model.current.rotation.y = Math.PI;

      model.current.traverse(o => {
        if (o.isMesh) {
          let type = o.name
          o.material = material.current
          if(type.includes("rim")){
            o.visible = true
          } else {
            o.visible = false
          }
        }
      });
      scene.add(model.current);
      setModelLoaded(true)
    }, undefined, (error) => {
      console.error(error);
    });

    const light = new THREE.DirectionalLight(0xffffff, 0.5);
    light.position.set(500, 500, 500);
    light.lookAt(0, 0, 0)
    scene.add(light);

    const color = 0xFFFFFF;
    const intensity = 1;
    const ambient = new THREE.AmbientLight(color, intensity);
    scene.add(ambient);

    var controls = new OrbitControls(camera, renderer.domElement);
    controls.maxPolarAngle = Math.PI;
    controls.minPolarAngle = 0;
    controls.enableDamping = true;
    controls.enablePan = false;
    controls.dampingFactor = 0.5;
    controls.autoRotate = false;
    controls.autoRotateSpeed = 0.1;


    let animate = () => {

      controls.update();
      renderer.render(scene, camera);
      requestAnimationFrame(animate);

      let resizeRendererToDisplaySize = (renderer) => {
        const canvas = renderer.domElement;
        var width = window.innerWidth;
        var height = window.innerHeight;
        var canvasPixelWidth = canvas.width / window.devicePixelRatio;
        var canvasPixelHeight = canvas.height / window.devicePixelRatio;
  
        const needResize = canvasPixelWidth !== width || canvasPixelHeight !== height;
        if (needResize) {
          renderer.setSize(width, height, false);
        }
        return needResize;
      }

      if (resizeRendererToDisplaySize(renderer)) {
        const canvas = renderer.domElement;
        camera.aspect = canvas.clientWidth / canvas.clientHeight;
        camera.updateProjectionMatrix();
      }

      if (model.current != null && !animationFinished) {
        initRotate++;
        if (initRotate <= 120) {
          model.current.rotation.y += Math.PI / 60;
        } else {
          setAnimationFinished(true)
        }
      }
    }

    let initRotate = 0;
    animate();

    return () => {
      sceneElement.current.removeChild(renderer.domElement)
    }
  }, [])

  useEffect(() => {
    if(model.current && modelLoaded){
      if (armSelected === 0) {
        hideElement(model.current, activeOption);
      } else {
        setMaterial(model.current, activeOption, armSelected, material.current);
      }
      if(props.handleSetArmVariation){
        props.handleSetArmVariation(armSelected)
      }
    }
  }, [armSelected, modelLoaded])

  useEffect(() => {
    if(model.current && modelLoaded){
      if (insertSelected === 0) {
        hideElement(model.current, activeOption);
      } else {
        setMaterial(model.current, activeOption, insertSelected, material.current);
      }
      if(props.handleSetInsertVariation){
        props.handleSetInsertVariation(insertSelected)
      }
    }
  }, [insertSelected, modelLoaded])

  const hideElement = (model, element) => {
    model.traverse(o => {
      if (o.isMesh && o.name != null) {
        if (o.name.includes(element)) {
          o.visible = false;
        }
      }
    });
  }

  const setMaterial = (parent, type, index, mtl) => {
    const elementName = type + "_" + index
    let found = false
    parent.traverse(o => {
      if (o.isMesh && o.name != null) {
        if (o.name === elementName) {
          found = true
          o.material = mtl;
          o.visible = true;
        } else if(o.name.includes(type)){
          o.visible = false;
        }
      }
    });

    if (!found){
      setAssetLoading(true)

      const loader = new GLTFLoader();

      loader.load(elementName + '.glb', (gltf) => {
          const mesh = gltf.scene.children[0]
          mesh.material = material.current
          model.current.add(mesh);
          for(let i = 1; i < 5; i++) {
            const clonded_mesh = mesh.clone();
            clonded_mesh.rotation.x += THREE.Math.degToRad(i * 72)
            model.current.add(clonded_mesh);
          }
          setAssetLoading(false)
        }, undefined, (error) => {
          console.error(error);
          setAssetLoading(false)
      });
    }
  }


  const handleClose = () => {
    if(props.close){
      props.close()
    }   
  }

  const [activeVariationIndex, changeActiveVariation] = variations[activeOption]


  return(
    <div ref={sceneElement} id="d5b73a-scene">
      { !modelLoaded && <BlockingLoader /> }
      { assetLoading && <NonBlockingLoader /> }

      <canvas ref={canvasElement} id="d5b73a-configurator-canvas"></canvas>
      <div className="d5b73a-closeButton" onClick={handleClose}>X</div>
      <div className="d5b73a-frame">
        <h1 className="d5b73a-frame__title">Inventa Konfigurator</h1>
      </div>
      <div className="d5b73a-configurator-drag-notice-parent">
        <span className={`d5b73a-configurator-drag-notice ${animationFinished ? "start" : ""}`}  id="js-drag-notice">Drag to rotate 360&#176;</span>
      </div>
      <div className="d5b73a-configurator-options">
        {
          ["arm", "insert", "paint"].map(element => 
            <OptionElement key={element} active={activeOption===element} icon={props.base_url + `icon/${element}.png`} onClick={()=>setActiveOption(element)}/>
          )
        }
      </div>
      <div className="d5b73a-configurator-controls">
          <div id="js-tray" className="d5b73a-configurator-tray">
              <div id="js-tray-slide" className="d5b73a-configurator-tray__slide">
                  { 
                    availableOptions[activeOption].map(
                      (element, index) =>  
                        <VariationElement {...props} key={activeOption + index} index={index} isActive={index === activeVariationIndex} element={element} onClick={() => changeActiveVariation(index)} />    
                    )
                  }
              </div>
          </div>
      </div>
    </div>
  )
}


const OptionElement = (props) => {
  return (
    <div className={`d5b73a-configurator-option ${props.active ? " --is-active" : ""}`} onClick={props.onClick}>
      <img src={props.icon} alt="" />
    </div>
  )
}

const VariationElement = (props) => {
  if(props.element.texture){
    return <div className={"d5b73a-configurator-variation " + (props.isActive? "--is-active": "")} onClick={props.onClick} style={{backgroundImage: "url(" + props.base_url +  props.element.texture + ")"}} /> 
  } else if (props.element.color){
    return( 
      <div className={"d5b73a-configurator-variation " + (props.isActive? "--is-active": "")} onClick={props.onClick}> 
        <div style={{backgroundColor: `#${props.element.color}`, width: '100%', height: '100%', marginTop: '5px'}}></div>
      </div>
    )
  } else {
    return <div className={"d5b73a-configurator-variation " + (props.isActive ? "--is-active": "")} onClick={props.onClick}>{props.index}</div> 
  }
}
